import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppStore } from '@runtime/core/store/appStore'
import { commitPosterThemeSelection } from '@runtime/core/theme/commitPosterThemeSelection'
import {
  consumePendingPosterTheme,
  readPendingPosterTheme,
  writePendingPosterTheme,
} from '@runtime/core/theme/pendingPosterTheme'
import { DEFAULT_POSTER_THEME_ID } from '@runtime/core/theme/posterThemes'
import { ThemeEngine } from '@runtime/core/theme/themeEngine'
import { readHomeSelection, withoutHomeSelection } from './homeArrival'
import { resolveHomePoster, type HomePosterResult } from './resolveHomePoster'

type Ready = Extract<HomePosterResult, { status: 'ready' }>
export type HomePosterState =
  | Exclude<HomePosterResult, { status: 'cancelled' }>
  | { status: 'loading' }

export function useHomePosterSelection() {
  const location = useLocation()
  const navigate = useNavigate()
  const storedId = useAppStore(s => s.themeId) || DEFAULT_POSTER_THEME_ID
  const [state, setState] = useState<HomePosterState>({ status: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const sequence = useRef(0)
  const ready = useRef<Ready | null>(null)
  const latestLocation = useRef(location)
  latestLocation.current = location

  const clearRequest = useCallback(() => {
    sequence.current += 1
    setAttempt(value => value + 1)
    const current = latestLocation.current
    navigate(
      { pathname: '/home', search: withoutHomeSelection(current.search), hash: current.hash },
      { replace: true, state: current.state },
    )
  }, [navigate])

  useEffect(() => {
    const token = ++sequence.current
    const current = () => token === sequence.current
    const selection = readHomeSelection(location.search)
    if (!selection && ready.current?.cfg.id === storedId && ThemeEngine.current() === storedId) {
      setState(ready.current)
      return
    }
    setState({ status: 'loading' })
    void resolveHomePoster(selection, storedId, current).then(result => {
      if (!current() || result.status === 'cancelled') return
      if (result.status === 'ready') {
        if (result.pending) writePendingPosterTheme(result.pending)
        ready.current = result
        commitPosterThemeSelection({
          themeId: result.cfg.id || storedId,
          cfg: result.cfg,
          setThemeId: useAppStore.getState().setThemeId,
        })
        if (readPendingPosterTheme()?.themeId === result.cfg.id) consumePendingPosterTheme()
        setState(result)
        if (selection) clearRequest()
      } else setState(result)
    })
    return () => {
      sequence.current += 1
    }
  }, [location.search, storedId, attempt, clearRequest])

  const useOriginal = () => {
    sequence.current += 1
    const current = latestLocation.current
    const search = withoutHomeSelection(current.search)
    navigate(
      {
        pathname: '/home',
        search: `${search}${search ? '&' : '?'}theme=${DEFAULT_POSTER_THEME_ID}`,
        hash: current.hash,
      },
      { replace: true, state: current.state },
    )
  }

  return { state, clearRequest, useOriginal, retry: () => setAttempt(value => value + 1) }
}
